import { collection, query, where, getDocs, doc, getDoc, orderBy, limit, updateDoc, increment } from 'firebase/firestore';
import { getFromPrice, getPricingUnits } from '../utils/unitAvailability';
import { db } from './firebase';
import type { Development, Property, PropertyType } from '../../../src/types/property.types';

/**
 * The public website reads the SANITIZED mirror of `developments`
 * (marketing fields only — no commissions, buyer/consultant IDs, banking
 * details or documents). The mirror is maintained by the
 * syncPublicDevelopment Cloud Function and keeps the SAME doc IDs, so
 * propertyDetailCms/{devId} lookups are unaffected.
 */
const PUBLIC_DEVELOPMENTS = 'publicDevelopments';

/**
 * Property Service for public website
 * Fetches property listings from Firestore
 */

/**
 * Helper function to check if a development is HQ
 */
function isHQDevelopment(facilitator?: string): boolean {
  return facilitator?.toLowerCase().includes('propinvest') ||
         facilitator?.toLowerCase() === 'hq' ||
         !facilitator ||
         facilitator?.trim() === '';
}

/**
 * Helper function to filter out HQ developments without marketing names
 * HQ developments MUST have marketing names to appear on public website
 */
function filterValidDevelopments(developments: Development[]): Development[] {
  return developments.filter(dev => {
    const isHQ = isHQDevelopment(dev.facilitator);
    // If it's HQ, it must have a marketing name to be shown
    if (isHQ && !dev.marketingName) {
      return false;
    }
    return true;
  });
}

/**
 * Fetch featured developments for homepage
 */
export async function getFeaturedDevelopments(): Promise<Development[]> {
  try {
    const q = query(
      collection(db, PUBLIC_DEVELOPMENTS),
      where('showOnPublicWebsite', '==', true),
      where('isFeatured', '==', true),
      orderBy('createdAt', 'desc'),
      limit(6)
    );

    const querySnapshot = await getDocs(q);
    const developments = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Development[];

    // Filter out HQ developments without marketing names
    return filterValidDevelopments(developments);
  } catch (error) {
    console.error('Error fetching featured properties:', error);
    // Fallback to active properties if featured query fails
    return getActiveProperties().then(props => props.slice(0, 6));
  }
}

/**
 * Fetch all public developments
 */
export async function getActiveProperties(): Promise<Development[]> {
  try {
    const q = query(
      collection(db, PUBLIC_DEVELOPMENTS),
      where('showOnPublicWebsite', '==', true),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const developments = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Development[];

    // Filter out HQ developments without marketing names
    return filterValidDevelopments(developments);
  } catch (error) {
    console.error('Error fetching active properties:', error);
    throw error;
  }
}

/**
 * Fetch properties by partner ID
 */
export async function getPropertiesByPartner(partnerId: string): Promise<Development[]> {
  try {
    const q = query(
      collection(db, PUBLIC_DEVELOPMENTS),
      where('partnerId', '==', partnerId),
      where('showOnPublicWebsite', '==', true),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const developments = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Development[];

    // Filter out HQ developments without marketing names
    return filterValidDevelopments(developments);
  } catch (error) {
    console.error('Error fetching partner properties:', error);
    throw error;
  }
}

/**
 * Fetch single development by ID (only if public)
 */
export async function getPropertyById(developmentId: string): Promise<Development | null> {
  try {
    const docRef = doc(db, PUBLIC_DEVELOPMENTS, developmentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Only return if property is marked for public website
      if (data.showOnPublicWebsite === true) {
        const development = {
          id: docSnap.id,
          ...data
        } as Development;

        // Check if HQ development has marketing name
        const isHQ = isHQDevelopment(development.facilitator);
        if (isHQ && !development.marketingName) {
          // HQ development without marketing name should not be shown
          return null;
        }

        return development;
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching property:', error);
    throw error;
  }
}

/**
 * Fetch single development by slug (only if public)
 */
export async function getPropertyBySlug(slug: string): Promise<Development | null> {
  try {
    const q = query(
      collection(db, PUBLIC_DEVELOPMENTS),
      where('slug', '==', slug),
      where('showOnPublicWebsite', '==', true),
      limit(1)
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    const docSnap = snapshot.docs[0];
    const development = {
      id: docSnap.id,
      ...docSnap.data()
    } as Development;

    // Check if HQ development has marketing name
    const isHQ = isHQDevelopment(development.facilitator);
    if (isHQ && !development.marketingName) {
      return null;
    }

    return development;
  } catch (error) {
    console.error('Error fetching property by slug:', error);
    throw error;
  }
}

/**
 * Resolve an unlisted campaign landing page token (/invest/{token}).
 * Single-field equality — the mirror only contains public developments,
 * so no extra visibility clause (and no composite index) is needed; the
 * HQ marketing-name gate still applies like every other reader.
 */
export async function getPropertyByLandingToken(token: string): Promise<Development | null> {
  try {
    const q = query(
      collection(db, PUBLIC_DEVELOPMENTS),
      where('landingPageToken', '==', token),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const docSnap = snapshot.docs[0];
    const development = { id: docSnap.id, ...docSnap.data() } as Development;
    if (isHQDevelopment(development.facilitator) && !development.marketingName) return null;
    return development;
  } catch (error) {
    console.error('Error resolving landing page token:', error);
    return null;
  }
}

/**
 * Search properties by filters
 */
export interface PropertyFilters {
  propertyType?: string;
  minPrice?: number;
  maxPrice?: number;
  city?: string;
  province?: string;
}

export async function searchProperties(filters: PropertyFilters): Promise<Development[]> {
  try {
    let q = query(
      collection(db, PUBLIC_DEVELOPMENTS),
      where('showOnPublicWebsite', '==', true)
    );

    // Add filters
    if (filters.propertyType) {
      q = query(q, where('propertyType', '==', filters.propertyType));
    }

    if (filters.city) {
      q = query(q, where('city', '==', filters.city));
    }

    if (filters.province) {
      q = query(q, where('province', '==', filters.province));
    }

    q = query(q, orderBy('createdAt', 'desc'), limit(50));

    const querySnapshot = await getDocs(q);
    let results = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Development[];

    // Filter out HQ developments without marketing names
    results = filterValidDevelopments(results);

    // Client-side price filtering (if needed)
    if (filters.minPrice) {
      results = results.filter(dev => {
        const minUnitPrice = getFromPrice(dev.units);
        if (minUnitPrice === null) return false;
        return minUnitPrice >= filters.minPrice!;
      });
    }

    if (filters.maxPrice) {
      results = results.filter(dev => {
        const pool = getPricingUnits(dev.units);
        if (pool.length === 0) return false;
        const maxUnitPrice = Math.max(...pool.map(u => u.price));
        return maxUnitPrice <= filters.maxPrice!;
      });
    }

    return results;
  } catch (error) {
    console.error('Error searching properties:', error);
    throw error;
  }
}

/**
 * Fetch all developments with optional filters
 */
export async function getAllDevelopments(filters?: {
  province?: string;
  propertyType?: string;
  minPrice?: number;
  maxPrice?: number;
  searchQuery?: string;
}): Promise<Development[]> {
  try {
    let q = query(
      collection(db, PUBLIC_DEVELOPMENTS),
      where('showOnPublicWebsite', '==', true)
    );

    if (filters?.province) {
      q = query(q, where('province', '==', filters.province));
    }

    if (filters?.propertyType) {
      q = query(q, where('propertyType', '==', filters.propertyType));
    }

    q = query(q, orderBy('createdAt', 'desc'));

    const snapshot = await getDocs(q);
    let developments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Development));

    // Filter out HQ developments without marketing names
    developments = filterValidDevelopments(developments);

    // Client-side filtering for price and search
    if (filters?.minPrice && developments.length > 0) {
      developments = developments.filter(d => {
        const minPrice = getFromPrice(d.units);
        if (minPrice === null) return false;
        return minPrice >= filters.minPrice!;
      });
    }

    if (filters?.maxPrice && developments.length > 0) {
      developments = developments.filter(d => {
        const minPrice = getFromPrice(d.units);
        if (minPrice === null) return false;
        return minPrice <= filters.maxPrice!;
      });
    }

    if (filters?.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      developments = developments.filter(d => {
        // For HQ developments, also search marketing name
        const isHQ = isHQDevelopment(d.facilitator);
        const marketingNameMatch = isHQ && d.marketingName && d.marketingName.toLowerCase().includes(query);

        return d.name?.toLowerCase().includes(query) ||
               d.streetAddress?.toLowerCase().includes(query) ||
               d.city?.toLowerCase().includes(query) ||
               d.province?.toLowerCase().includes(query) ||
               marketingNameMatch;
      });
    }

    return developments;
  } catch (error) {
    console.error('Error fetching all developments:', error);
    throw error;
  }
}

// ============================================================================
// INDIVIDUAL PROPERTY LISTINGS (Partner Properties)
// ============================================================================

/**
 * Fetch all public individual property listings
 */
export async function getPublicPropertyListings(filters?: {
  propertyType?: PropertyType | PropertyType[];
  listingType?: 'sale' | 'rent';
  province?: string;
  city?: string;
  minPrice?: number;
  maxPrice?: number;
  minBedrooms?: number;
  searchQuery?: string;
}): Promise<Property[]> {
  try {
    let q = query(
      collection(db, 'properties'),
      where('showOnPublicWebsite', '==', true),
      where('status', '==', 'active')
    );

    // Add server-side filters where possible
    if (filters?.province) {
      q = query(q, where('province', '==', filters.province));
    }

    if (filters?.city) {
      q = query(q, where('city', '==', filters.city));
    }

    if (filters?.listingType) {
      q = query(q, where('listingType', '==', filters.listingType));
    }

    if (filters?.propertyType && !Array.isArray(filters.propertyType)) {
      q = query(q, where('propertyType', '==', filters.propertyType));
    }

    q = query(q, orderBy('createdAt', 'desc'), limit(100));

    const snapshot = await getDocs(q);
    let properties = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Property[];

    // Client-side filtering for complex queries
    if (filters?.propertyType && Array.isArray(filters.propertyType)) {
      properties = properties.filter(p => (filters.propertyType as PropertyType[]).includes(p.propertyType));
    }

    if (filters?.minPrice !== undefined) {
      properties = properties.filter(p => p.price >= filters.minPrice!);
    }

    if (filters?.maxPrice !== undefined) {
      properties = properties.filter(p => p.price <= filters.maxPrice!);
    }

    if (filters?.minBedrooms !== undefined) {
      properties = properties.filter(p => (p.bedrooms || 0) >= filters.minBedrooms!);
    }

    if (filters?.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      properties = properties.filter(p =>
        p.title.toLowerCase().includes(query) ||
        p.description.toLowerCase().includes(query) ||
        p.city.toLowerCase().includes(query) ||
        p.suburb?.toLowerCase().includes(query) ||
        p.streetAddress.toLowerCase().includes(query)
      );
    }

    return properties;
  } catch (error) {
    console.error('Error fetching public property listings:', error);
    throw error;
  }
}

/**
 * Fetch featured property listings
 */
export async function getFeaturedPropertyListings(limit_count: number = 6): Promise<Property[]> {
  try {
    const q = query(
      collection(db, 'properties'),
      where('showOnPublicWebsite', '==', true),
      where('status', '==', 'active'),
      where('isFeatured', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limit_count)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Property[];
  } catch (error) {
    console.error('Error fetching featured property listings:', error);
    throw error;
  }
}

/**
 * Fetch single property listing by ID
 */
export async function getPropertyListingById(propertyId: string): Promise<Property | null> {
  try {
    const docRef = doc(db, 'properties', propertyId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Only return if property is active and public
      if (data.showOnPublicWebsite === true && data.status === 'active') {
        // Increment view count
        try {
          await updateDoc(docRef, {
            viewCount: increment(1)
          });
        } catch (e) {
          // Ignore view count errors
        }

        return {
          id: docSnap.id,
          ...data
        } as Property;
      }
    }

    return null;
  } catch (error) {
    console.error('Error fetching property listing:', error);
    throw error;
  }
}

/**
 * Fetch property listings by partner ID
 */
export async function getPartnerPropertyListings(partnerId: string): Promise<Property[]> {
  try {
    const q = query(
      collection(db, 'properties'),
      where('partnerId', '==', partnerId),
      where('showOnPublicWebsite', '==', true),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Property[];
  } catch (error) {
    console.error('Error fetching partner property listings:', error);
    throw error;
  }
}
